import { OnTrackConditions } from "@common/models/v2";
import { Step } from "./steps/v2/step";
import invariant from "ts-invariant";
import {
  OnTrackConditionCheckResult,
  OnTrackConditionCheckResultWithElement,
  watchOnTrackConditions,
} from "./onTrackConditionCheck";
import { makeAutoObservable, runInAction } from "mobx";
import { debounce, memoize } from "lodash-es";
import { EventEmitter } from "../events/eventEmitter";
import { TourTrackManagerEvents, TourTrackManagerNamespace } from "./tourTrackManager.events";
import { WaitForElementStep } from "./steps/v2/waitForElement";

export class TourTrackManager {
  events = new EventEmitter<typeof TourTrackManagerNamespace, TourTrackManagerEvents>(TourTrackManagerNamespace);
  currentStep?: Step;
  detailedResults: Record<string, OnTrackConditionCheckResult[]> = {};
  results: Record<string, boolean> = {};
  targetElements: Record<string, Element | null> = {};
  stopWatch?: () => void;

  static readonly defaultWanderingOffTimeout = 3000;
  static readonly defaultTargetNotFoundTimeout = 3000;

  constructor() {
    makeAutoObservable(this);
  }

  setCurrentStep(step: Step) {
    if (step.id === this.currentStep?.id) {
      return;
    }

    this.currentStep = step;
    this.watchOnTrackConditions(step);
  }

  clearCurrentStep() {
    this.emitLastStatus();
    this.stopWatch?.();
    this.currentStep = undefined;
  }

  private watchOnTrackConditions(step: Step) {
    this.stopWatch?.();

    const onTrackConditions = step.attributes.onTrackConditions;

    this.results[step.id] = false;
    this.detailedResults[step.id] = this.buildInitialDetailedResults(onTrackConditions);

    this.stopWatch = watchOnTrackConditions(onTrackConditions, {
      onResultChange: (isOnTrack: boolean) => {
        this.handleResultChange(step, isOnTrack);
      },
      onConditionResultChange: (result: OnTrackConditionCheckResult) => {
        this.handleConditionResultChange(step, result);
      },
      onTargetElementChange: (result: OnTrackConditionCheckResultWithElement) => {
        this.handleTargetElementChange(step, result.element);
      },
    });
  }

  private handleResultChange(step: Step, isOnTrack: boolean) {
    runInAction(() => {
      this.results[step.id] = isOnTrack;
    });

    this.runOnWanderingOff(step)(() => {
      this.events.emit("wanderedOff", step);
    });

    this.runOnTargetNotFound(step)((reasons) => {
      this.events.emit("targetNotFound", { step, reasons });
    });

    this.runOnTrackSatisfied(step, () => {
      this.events.emit("trackSatisfied", step);
    });
  }

  private handleConditionResultChange(step: Step, result: OnTrackConditionCheckResult) {
    runInAction(() => {
      const detailedResults = this.detailedResults[step.id];
      const index = detailedResults.findIndex((r) => r.condition.id === result.condition.id);
      detailedResults[index] = result;
    });
  }

  private handleTargetElementChange(step: Step, element: Element | null) {
    runInAction(() => {
      this.targetElements[step.id] = element;
    });
  }

  /**
   * Returns a debounced function that triggers a callback when a user is "wandering off"
   * from the current step. The debounce is used to delay the initiation of the wandering
   * off signal, providing a time window for the user to come back on track before the
   * callback is executed. The debounce timeout is dynamically determined based on the step type.
   *
   * Since the debounce timeout is dynamically determined, it is important to not create
   * a new debounce function on every call. `memoize` is used to cache the debounced function
   * per step (identified by `step.id`), ensuring consistent behavior by avoiding redundant
   * debounced function creation.
   */
  private runOnWanderingOff = memoize(
    (step: Step) => {
      return debounce(
        (callback: () => void) => {
          if (this.isEqualToCurrentStep(step.id) && this.isWanderingOff(step.id)) {
            callback();
          }
        },
        this.getWanderingOffTimeout(step),
        { leading: false, trailing: true }
      );
    },
    (step) => step.id
  );

  private runOnTargetNotFound = memoize(
    (step: Step) => {
      return debounce(
        (callback: (reasons: string[]) => void) => {
          const { isTargetNotFound, reasons } = this.isTargetNotFound(step.id);
          if (this.isEqualToCurrentStep(step.id) && isTargetNotFound) {
            callback(reasons);
          }
        },
        this.getTargetNotFoundTimeout(step),
        { leading: false, trailing: true }
      );
    },
    (step) => step.id
  );

  private runOnTrackSatisfied = (step: Step, callback: () => void) => {
    if (this.isEqualToCurrentStep(step.id) && this.isOnTrack(step.id)) {
      callback();
    }
  };

  private emitLastStatus() {
    const lastStep = this.currentStep;
    if (!lastStep) return;

    const isOnTrack = this.isOnTrack(lastStep.id);
    if (isOnTrack) return;

    const isTargetNotFound = this.isTargetNotFound(lastStep.id);
    if (isTargetNotFound) {
      const targetAvailability = this.isTargetElementAvailable(lastStep.id);
      this.events.emit("targetNotFound", { step: lastStep, reasons: targetAvailability.reasons });
      return;
    }

    const isWanderingOff = this.isWanderingOff(lastStep.id);
    if (isWanderingOff) {
      this.events.emit("wanderedOff", lastStep);
    }
  }

  isOnTrack(stepId: string) {
    return this.results[stepId];
  }

  isWanderingOff(stepId: string) {
    return !this.isOnCorrectPage(stepId);
  }

  isTargetNotFound(stepId: string): { isTargetNotFound: boolean; reasons: string[] } {
    const isOnCorrectPage = this.isOnCorrectPage(stepId);
    const isTargetElementAvailable = this.isTargetElementAvailable(stepId);
    return {
      isTargetNotFound: isOnCorrectPage && !isTargetElementAvailable.isAvailable,
      reasons: isTargetElementAvailable.reasons,
    };
  }

  getTargetElement(stepId: string) {
    if (!this.isOnTrack(stepId)) {
      return null;
    }
    return this.targetElements[stepId];
  }

  stop() {
    this.stopWatch?.();
  }

  private isOnCorrectPage(stepId: string) {
    const result = this.mustGetResult(stepId);
    if (result) {
      return true;
    }

    const detailedResults = this.mustGetDetailedResult(stepId);

    const urlConditions = detailedResults.filter((r) => r.condition.isUrlCondition());
    const supplementarySelectorConditions = detailedResults.filter((r) =>
      r.condition.isSupplementarySelectorCondition()
    );
    const pageOnTrackConditions = urlConditions.concat(supplementarySelectorConditions);

    return pageOnTrackConditions.every((r) => r.isSatisfied);
  }

  private isTargetElementAvailable(stepId: string): { isAvailable: boolean; reasons: string[] } {
    const result = this.mustGetResult(stepId);
    if (result) {
      // If overall result is true, target element is considered available with no specific failure reasons
      return { isAvailable: true, reasons: [] };
    }

    const detailedResults = this.mustGetDetailedResult(stepId);
    const targetElementConditions = detailedResults.filter((r) => r.condition.isTargetSelectorCondition());

    // FIX: For steps with no target conditions (like filler steps), target is always "available"
    // since they don't require any DOM elements to be found
    if (targetElementConditions.length === 0) {
      return { isAvailable: true, reasons: [] };
    }

    const unsatisfiedConditions = targetElementConditions.filter((r) => !r.isSatisfied);

    if (unsatisfiedConditions.length === 0) {
      return { isAvailable: true, reasons: [] };
    } else {
      const failureReasons = unsatisfiedConditions.map((r) => r.reason).filter((reason): reason is string => !!reason); // Filter out null/undefined reasons and type guard
      return { isAvailable: false, reasons: failureReasons };
    }
  }

  getResult(stepId: string) {
    return this.results[stepId];
  }

  getDetailedResult(stepId: string) {
    return this.detailedResults[stepId];
  }

  private getWanderingOffTimeout(step: Step) {
    if (step instanceof WaitForElementStep) {
      return step.attributes.timeout;
    }

    return TourTrackManager.defaultWanderingOffTimeout;
  }

  private getTargetNotFoundTimeout(step: Step) {
    if (step instanceof WaitForElementStep) {
      return step.attributes.timeout;
    }

    return TourTrackManager.defaultTargetNotFoundTimeout;
  }

  private mustGetResult(stepId: string) {
    const result = this.getResult(stepId);
    invariant(result !== undefined, "step on track conditions not checked yet");

    return result;
  }

  private mustGetDetailedResult(stepId: string) {
    const detailedResult = this.getDetailedResult(stepId);
    invariant(detailedResult !== undefined, "step on track conditions not checked yet");

    return detailedResult;
  }

  private isEqualToCurrentStep(stepId: string) {
    return this.currentStep?.id === stepId;
  }

  private buildInitialDetailedResults(onTrackConditions: OnTrackConditions) {
    return onTrackConditions.nonEmptyConditions.map((condition) => ({
      condition,
      isSatisfied: false,
      reason: "not_evaluated",
    }));
  }
}
